import { motion } from "framer-motion";
import { useCallback } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import ScrollIndicator from "@/components/landing/ScrollIndicator";
import heroBgVideo from "@/assets/diagnostic-hero-bg.mp4";

const DiagnosticHero = () => {
  const scrollToCalendly = useCallback(() => {
    const calendlySection = document.getElementById("calendly");
    if (calendlySection) {
      calendlySection.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  const scrollToTest = () => {
    const testSection = document.getElementById("test");
    if (testSection) {
      testSection.scrollIntoView({
        behavior: "smooth"
      });
    }
  };

  return (
    <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden bg-black">
      {/* Background video */}
      <div className="absolute inset-0">
        <video 
          src={heroBgVideo} 
          autoPlay 
          loop 
          muted 
          playsInline
          className="w-full h-full object-cover"
        />
        {/* Blue glow overlay */}
        <div 
          className="absolute inset-0" 
          style={{ 
            background: "radial-gradient(ellipse 80% 100% at 0% 50%, rgba(37, 99, 235, 0.4) 0%, rgba(37, 99, 235, 0.15) 30%, transparent 70%)" 
          }} 
        />
        <div className="absolute inset-0 bg-black/30" />
      </div>

      {/* Content */}
      <div className="container relative z-10 px-4 py-20">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
          className="flex flex-col items-center text-center max-w-4xl mx-auto"
        >
          {/* Minimal badge */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mb-12"
          >
            <span className="text-xs tracking-[0.3em] uppercase text-neutral-500 font-medium">
              CORESSEN CAPITAL
            </span>
          </motion.div>

          {/* Main Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="font-display text-3xl md:text-5xl lg:text-6xl font-medium leading-[1.15] mb-8 text-white tracking-tight max-w-[22ch] md:max-w-[24ch]"
          >
            Transformamos empresas y líderes con trayectoria en un{" "}
            <span className="text-sky-300">activo de autoridad y valor</span>.
          </motion.h1>

          {/* Elegant divider */}
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="w-16 h-px mb-8"
            style={{ background: "linear-gradient(to right, transparent, hsl(214, 95%, 77% / 0.5), transparent)" }}
          />

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="text-base md:text-xl text-neutral-400 max-w-2xl mb-14 leading-relaxed font-light"
          >
            Proyectamos el prestigio, elevamos la percepción y blindamos el negocio a través de nuestro método de{" "}
            <span className="text-white">Capital de Marca®</span>.
            <br />
            <br />
            Llegamos al núcleo para elevar lo esencial.
          </motion.p>


          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
          >
            <Button
              variant="gold"
              size="xl"
              onClick={scrollToCalendly}
              className="tracking-wider font-semibold rounded-full group"
            >
              AGENDAR LLAMADA
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
            </Button>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2">
        <ScrollIndicator onClick={scrollToTest} />
      </div>
    </section>
  );
};

export default DiagnosticHero;
